import React, { ChangeEvent, ReactElement, useEffect, useMemo } from 'react';
import { TextField } from '@mui/material';
import { Autocomplete } from '@mui/material';
import { filterSelectedLabels, getLabelOptionString, isLabelSelected } from './AutocompleteHelper';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { RootState } from '../../../redux/reducers';
import {
    addNewLabelEntity,
    createNewArticleLabel,
    importArticleLabels,
    updateLabelTitle,
} from '../../../redux/actions/labelActions';

type ArticleLabelAutocompleteProps = {
    isRequired: boolean;
};

export default function ArticleLabelAutocomplete(props: ArticleLabelAutocompleteProps): ReactElement {
    const { isRequired } = props;
    const articleLabels = useAppSelector((state: RootState) => state.labels.articleLabels);

    const label = useAppSelector((state) => state.label.currentLabel);
    const labelEntities = useAppSelector((state) => state.label.labelEntities);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!label) {
            dispatch(createNewArticleLabel());
        }
    }, [dispatch]);

    useEffect(() => {
        if (!articleLabels) {
            dispatch(importArticleLabels());
        }
    }, [dispatch]);

    const handleLabelSelect = (event: ChangeEvent<unknown>, value: string | null): void => {
        if (value !== null) {
            const searchedLabel = articleLabels?.find((a) => {
                return isLabelSelected(a, value);
            });

            if (!!searchedLabel && !!searchedLabel.id && searchedLabel?.id >= 0) {
                dispatch(addNewLabelEntity(searchedLabel));
            } else {
                if (!!label) {
                    dispatch(addNewLabelEntity(label));
                }
            }
        }
    };

    const handleLabelTitleChange = (event: ChangeEvent<{ value: unknown }>): void => {
        const searchToken: string = (event.target.value as string) ?? '';

        dispatch(updateLabelTitle(searchToken));
    };

    const selectedValues = useMemo(() => {
        return label?.id ? getLabelOptionString(articleLabels?.find((l) => l.id === label.id)) : null;
    }, [articleLabels, label]);

    const optionsValues = useMemo(() => {
        return articleLabels?.filter((al) => !labelEntities?.find((le) => le.label?.id === al.id));
    }, [articleLabels, labelEntities, label]);

    return (
        <Autocomplete
            freeSolo
            options={optionsValues?.map((option) => getLabelOptionString(option)) ?? []}
            filterOptions={(x) => {
                return !!label?.title ? filterSelectedLabels(x, label?.title) : [];
            }}
            value={selectedValues}
            clearOnEscape
            onChange={handleLabelSelect}
            renderInput={(params) => (
                <TextField
                    {...params}
                    autoFocus
                    required={isRequired}
                    error={isRequired && (!label?.title || label.title.trim() === '')}
                    fullWidth
                    label="Tag"
                    variant="outlined"
                    style={{ backgroundColor: '#f7f7f7', borderRadius: 5 }}
                    onChange={handleLabelTitleChange}
                />
            )}
        />
    );
}
